import * as React from 'react';
import type { FieldPath, FieldValues, UseFormGetValues, UseControllerProps } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import type { ColorPalette } from '@acceligentllc/shared/enums/color';

import type { Position, Alignment } from 'af-components/Controls/ColorPicker/ColorsGrid';

import ColorPickerCell from '../../RHFTableCellComponents/ColorPicker/ColorPickerCell';
import type { CellError } from '../../RHFTableCellComponents/error.types';
import RHFUnregisteredFieldWrapper from '../RHFUnregisteredFieldWrapper';

type Props<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues>,
	TTransformedValues
> = UseControllerProps<TFieldValues, TName, TTransformedValues> & {
	rhfId: number | string;
	getValues: UseFormGetValues<TFieldValues>;
	position: Position;
	align: Alignment;
	showAdditionalOptions?: boolean;
	readonly?: boolean;
	disabled?: boolean;
	fixed?: boolean;
	onChange?: (color: ColorPalette) => void;
};

export function ColorPicker<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues
>(props: Props<TFieldValues, TName, TTransformedValues>) {
	const {
		control,
		name,
		rhfId,
		position,
		align,
		showAdditionalOptions = false,
		readonly = false,
		disabled = false,
		defaultValue,
		getValues,
		fixed,
		onChange,
	} = props;

	const watchedValue = useWatch({ control, name });

	const storeValue = getValues(name);

	const isDisabled = React.useMemo(() => {
		return readonly || disabled;
	}, [disabled, readonly]);

	const resolvedValue = React.useMemo(() => {
		let _value = defaultValue;

		if (storeValue !== undefined) {
			_value = storeValue;
		}

		if (watchedValue !== undefined) {
			_value = watchedValue;
		}

		return _value ?? null as Nullable<ColorPalette>;
	}, [defaultValue, storeValue, watchedValue]);

	const render = React.useCallback((
		{ error }: {
			error: Nullable<CellError>;
		}
	) => {
		const key = `${name}_${rhfId}_${resolvedValue}`;

		return (
			<ColorPickerCell
				align={align}
				disabled={isDisabled}
				error={error ?? undefined}
				fixed={fixed}
				key={key}
				onChange={onChange}
				position={position}
				showAdditionalOptions={showAdditionalOptions}
				value={resolvedValue}
			/>
		);
	}, [align, fixed, isDisabled, name, onChange, position, resolvedValue, rhfId, showAdditionalOptions]);

	return (
		<RHFUnregisteredFieldWrapper<TFieldValues, TName, TTransformedValues>
			control={control}
			getValues={getValues}
			key={`${name}_${rhfId}`}
			name={name}
			render={render}
		/>
	);
}
